import {useMemo} from "react";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import type {DataGridQueryState} from "~/components/data-grid/useDataGridQueryState";
import type {SupplierClusterMember, SupplierDataSource} from "~/pages/supplier-360/types/Supplier360Types";
import {useDataSourcesQuery} from "~/pages/supplier-360/hooks/useDataSourcesQuery";
import {getSupplierRawColumns} from "./SupplierRawColumns";

const NO_DATA_SOURCES: SupplierDataSource[] = [];

export type SupplierRawTableProps = {
    rows: SupplierClusterMember[];
    isLoading: boolean;
    rowCount: number;
    /** Pagination / filter / sort state from `useDataGridQueryState`. */
    gridState: DataGridQueryState;
    /** Page-level search term in effect — narrows the rows without going through `filterModel`. */
    isSearchActive?: boolean;
    onOpenEntity: (entityId: number) => void;
    title?: string;
};

export const SupplierRawTable = ({
                                     rows,
                                     isLoading,
                                     rowCount,
                                     gridState,
                                     isSearchActive = false,
                                     onOpenEntity,
                                     title,
                                 }: SupplierRawTableProps) => {
    const {hasActiveFilters} = gridState;
    // A search narrows the rows just like a column filter, so an empty result is "no match", not "no data".
    const isNarrowed = hasActiveFilters || isSearchActive;
    // The Source filter's options (`data/source/`, §2.24). Fetched here rather than by the page:
    // this table is the only consumer and only mounts in the Raw view. Empty until they load.
    const {data: dataSources = NO_DATA_SOURCES} = useDataSourcesQuery();
    const columns = useMemo(() => getSupplierRawColumns(onOpenEntity, dataSources), [onOpenEntity, dataSources]);

    return (
        <CustomDataGrid
                layoutKey="supplier-360-raw"
                rows={rows}
                columns={columns}
                loading={isLoading}
                rowCount={rowCount}
                {...gridState.dataGridProps}
                // Text and number filters run server-side, so don't fire one request per keystroke.
                filterDebounceMs={500}
                showToolbar
                disableRowSelectionOnClick
                toolbarProps={{
                    title: title,
                    showFilterPanel: false,
                }}
                localeText={isNarrowed && rows.length === 0 && !isLoading
                    ? {noRowsLabel: 'No raw rows match the current filters.'}
                    : undefined}
        />
    );
};
